import { Platform } from 'react-native'

import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { cmsApi } from '@zet/cms-network'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { APP_NAME } from '@zet/end-points'

import useFilterStore from './useFilterStore'

type Filters = {
  groupCode?: string
  benefits?: string
  cardFees?: string
  employmentType?: string
  incomeRange?: string
  quickFilter?: string
  categoryId?: number
  [key: string]: any
}

type CategoryBanner = {
  id: number
  media_for_the_banner: {
    id: number
    redirect_to_screen?: string
    screenOptions?: any
    image: {
      id: number
      url: string
    }
  }
}

function transformAndStringify(input?: Filters): string {
  const keysToKeep = [
    'groupCode',
    'benefits',
    'cardFees',
    'employmentType',
    'incomeRange',
    'quickFilter',
  ]

  const result: Record<string, string[]> = {}

  if (!input) return ''

  keysToKeep.forEach((key) => {
    if (input[key]) {
      result[key] = input[key].split(',')
    }
  })

  return encodeURIComponent(JSON.stringify(result))
}

async function getCategoryBanner({
  currentCategoryId,
  filters,
}: {
  currentCategoryId: number
  filters: string
}): Promise<CategoryBanner[]> {
  const { data } = await cmsApi.get(
    `cms/api/product-filter-banners?categoryId=${Number(
      currentCategoryId
    )}&filters=${filters}`,
    {
      headers: {
        Authorization: null,
      },
    }
  )
  return data?.data
}

const useGetCategoryBanner = ({ categoryId }: { categoryId: number }) => {
  const selectedFiltersTransformed = useFilterStore(
    (state) => state?.selectedFiltersTransformed as Filters | undefined
  )
  const isWeb = Platform.OS === 'web'

  const currentCategoryId = selectedFiltersTransformed?.categoryId || categoryId
  const enabled =
    !isWeb && APP_NAME !== MY_TEAMS_ADVISOR && Boolean(currentCategoryId)
  const filters = transformAndStringify(selectedFiltersTransformed)

  const queryKey = ['getCategoryBanners', currentCategoryId, filters]

  const queryData: UseQueryResult<CategoryBanner> = useQuery(
    queryKey,
    () => getCategoryBanner({ currentCategoryId, filters }),
    {
      refetchOnWindowFocus: false,
      enabled,
    }
  )

  return {
    ...queryData,
    isLoading: queryData.isLoading && queryData.fetchStatus !== 'idle',
  }
}

export default useGetCategoryBanner
